import { useSelector, useDispatch } from 'react-redux';
import {
    createPlace,
    updatePlace,
    resetActivePlace,
    closeLoadingErrorDialog,
    getActivePlace,
    getPlaceDialogState,
    getPlaceUploadState,
} from '../../store/slices/placesSlice';
import { useTranslation } from 'react-i18next';
import settings from '../../config.json';
import useMediaQuery from '../../hooks/useMediaQuery';
import { Grid, TextField } from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import SubmitResetButtonGroup from '../../components/buttons/SubmitResetButtonGroup';
import CreatedUpdatedField from '../../components/form/CreatedUpdatedField';
import ErrorLoadingDataDialog from '../../components/dialogs/ErrorLoadingDataDialog';;


const placeSchema = yup.object().shape({
    place_name: yup
        .string()
        .required('form.error.field_required'),
    place_number: yup
        .string()
        .required('form.error.field_required'),
    process: yup
        .string(),
    description: yup
        .string()
});


function FormPlace() {

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const isAboveMobileSize = useMediaQuery(settings.NON_MOBILE_MEDIA_QUERY);

    const activePlace = useSelector(getActivePlace);
    const placeDialogState = useSelector(getPlaceDialogState);
    const uploadState = useSelector(getPlaceUploadState);

    const gridTextFieldStyle = { height: '66px', px: '15px' };
    const gridMultilineFieldStyle = { height: '95px', px: '15px' };
    const gridCreatedUpdatedStyle = { px: '15px' };

    /* ----- CREATE / UPDATE -------------------------------------------------------------*/

    const handleFormSubmit = async (values) => {

        const place = {
            place_id: activePlace.place_id,
            place_name: values.place_name,
            place_number: values.place_number,
            process: values.process,
            description: values.description,
            status: values.status,
            action: values.action,
            support: values.support,
        };

        if (placeDialogState.mode === 'add') {
            dispatch(createPlace(place));
        };
        if (placeDialogState.mode === 'edit') {
            dispatch(updatePlace(place));
        };
    };

    /* ----- RESET -----------------------------------------------------------------------*/

    // Pressing "ADD NEXT" button is not clearing inputs - expected behavior
    // Manual Reset added for clearing inputs in 'add' mode
    // 'edit' mode is using 'Formik' builded-in functionality

    const handleResetClick = () => {
        if (placeDialogState.mode === 'add') {
            dispatch(resetActivePlace());
        };
    };

    /* -----------------------------------------------------------------------------------*/


    return (
        <>
            <Formik
                onSubmit={handleFormSubmit}
                initialValues={activePlace}
                validationSchema={placeSchema}
                enableReinitialize
            >
                {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                    <Form>

                        <Grid
                            container
                            height={isAboveMobileSize ? 'auto' : '360px'} // set height order: --- 3/3 ---
                            p={isAboveMobileSize ? '6px 0 0 0' : '6px 0 12px 0'}
                            overflow='auto'
                        >
                            <Grid
                                item
                                xs={12}
                                sx={gridTextFieldStyle}
                            >
                                <TextField
                                    fullWidth
                                    variant='outlined'
                                    type='text'
                                    size="small"
                                    label={t('label.place_name')}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.place_name || ''}
                                    name='place_name'
                                    error={!!touched.place_name && !!errors.place_name}
                                    helperText={touched.place_name && <>{t(errors.place_name)}</>}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={gridTextFieldStyle}
                            >
                                <TextField
                                    fullWidth
                                    variant='outlined'
                                    type='text'
                                    size="small"
                                    label={t('label.place_number')}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.place_number || ''}
                                    name='place_number'
                                    error={!!touched.place_number && !!errors.place_number}
                                    helperText={touched.place_number && <>{t(errors.place_number)}</>}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={gridTextFieldStyle}
                            >
                                <TextField
                                    fullWidth
                                    variant='outlined'
                                    type='text'
                                    size="small"
                                    label={t('label.process')}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.process || ''}
                                    name='process'
                                    error={!!touched.process && !!errors.process}
                                    helperText={touched.process && <>{t(errors.process)}</>}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={gridMultilineFieldStyle}
                            >
                                <TextField
                                    multiline
                                    rows={3}
                                    fullWidth
                                    variant='outlined'
                                    type='text'
                                    size="small"
                                    label={t('label.description')}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.description || ''}
                                    name='description'
                                    error={!!touched.description && !!errors.description}
                                    helperText={touched.description && <>{t(errors.description)}</>}
                                />
                            </Grid>
                            {placeDialogState.mode === 'edit' ?
                                <Grid
                                    item
                                    xs={12}
                                //mt='-4px'
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sx={gridCreatedUpdatedStyle}
                                    >
                                        <CreatedUpdatedField
                                            label={t('label.created')}
                                            date={activePlace.created}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={gridCreatedUpdatedStyle}
                                    >
                                        <CreatedUpdatedField
                                            label={t('label.updated')}
                                            date={activePlace.updated}
                                        />
                                    </Grid>
                                </Grid>
                                :
                                null
                            }
                        </Grid>

                        <SubmitResetButtonGroup
                            onSubmit={handleSubmit}
                            onReset={handleResetClick}
                            labelSubmit={placeDialogState.mode === 'add' ? t('button.submit') : t('button.edit')}
                            labelReset={placeDialogState.mode === 'add' ? t('button.clear') : t('button.reset')}
                            loading={uploadState.status === 'loading'}
                        />

                    </Form>
                )}
            </Formik>

            <ErrorLoadingDataDialog
                open={uploadState.status === 'failed'}
                error={uploadState.error}
                onClose={() => dispatch(closeLoadingErrorDialog())}
            />
        </>
    );
};

export default FormPlace;